import { Backdrop, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { StyledButton, StyledButtonOutlined } from '../Header/stylesHeader';

export interface Lesson {
  id: number;
  title: string;
  description: string;
  path?: string;
}

const lessons: Lesson[] = [
  {
    id: 0,
    title: 'Support',
    description:
      '📖 Need help? Here you can always find guides, answers to common questions, and direct support from our team. If you ever get stuck, this is the place to get quick assistance and continue working without interruptions.',
  },
  {
    id: 1,
    title: 'Add Telegram bot',
    description:
      '🤖 Stay connected anywhere. By linking our Telegram bot, you’ll instantly receive alerts about new listings, client requests, and important updates — all directly in your messenger. No need to constantly check the platform.',
  },
  {
    id: 2,
    title: 'Add listing',
    description:
      '🏠 Showcase your properties. Add your real estate listings in just a few clicks. Fill in the details, upload photos, and make your property visible to hundreds of agents and agencies. The more listings you add, the more chances you have to close deals faster.',
  },
  {
    id: 3,
    title: 'Add client request',
    description:
      '📌 Work with real demand. Create a client request when you know what your client is looking for. All of our users will be notified about it and match it with the best available listings, saving you time and helping you impress your client with quick results.',
  },
  {
    id: 4,
    title: 'Secondary',
    path: '/',
    description:
      '🔑 Work with the secondary market. Explore and manage resale properties. Use this section to access opportunities beyond off-plan deals, giving your clients more choice and flexibility.',
  },
  {
    id: 5,
    title: 'Rent',
    path: '/ads/rent',
    description:
      '🏘 Rental opportunities at your fingertips. Add or search for rental properties. Whether your clients are relocating or looking for a temporary stay, you can find and share options with them quickly.',
  },
  {
    id: 6,
    title: 'Client requests',
    path: '/client-requests',
    description:
      '🔍 Track and manage all client needs. In this section, you’ll see every client request you’ve created or received. Keep them organized, respond fast, and never lose a potential deal.',
  },
  {
    id: 7,
    title: 'My properties',
    path: '/ads/my',
    description:
      '📂 Your portfolio in one place. Manage all your listings, update details, mark them as active/inactive, and keep everything tidy. Staying organized means more trust from your clients and partners.',
  },
  {
    id: 8,
    title: 'Messaging',
    path: '/chat',
    description:
      '💬 Communicate without leaving the platform. Send and receive messages from brokers, agents, and partners directly. No more switching between apps — everything you need is right here.',
  },
  {
    id: 9,
    title: 'Notifications',
    path: '/notifications',
    description:
      '🔔 Stay updated instantly. Every time there’s a new client request, listing update, or message, you’ll get a notification. This ensures you never miss an opportunity.',
  },
  // {
  //   id: 10,
  //   title: 'Referrals',
  //   path: '/referrals',
  //   description:
  //     '🌐 Invite & grow together. Share OnlineProperty with your colleagues and partners. When they join, both of you benefit — more listings, more requests, more chances to close deals. In addition you will get money bonus.',
  // },
  {
    id: 10,
    title: 'Friends',
    path: '/friends',
    description:
      '👥 Build your network. Add other agents and agencies as friends to easily collaborate, share opportunities, and strengthen your professional connections.',
  },
  {
    id: 11,
    title: 'Favorites/Collections',
    path: '/favorites',
    description:
      '⭐ Save what matters most. Organize listings into collections, mark favorites, and come back to them whenever you need. Perfect for keeping track of hot properties for specific clients.',
  },
  {
    id: 12,
    title: 'Save Filters & Turn Them Into Client Requests',
    path: '/saved-filters',
    description:
      '🔍 Stop setting up the same search twice. Apply your filters once, save them as a template, and reuse them in one click. Need to match a buyer? Convert any saved filter into a client request instantly — the criteria carry over automatically.',
  },
];

export const CustomBackdrop = ({
  openHeaderBackdrop,
  setOpenHeaderBackdrop,
  openSidebarBackdrop,
  setOpenSidebarBackdrop,
  count,
  setCount,
}: {
  openHeaderBackdrop?: boolean;
  setOpenHeaderBackdrop?: (value: boolean) => void;
  openSidebarBackdrop?: boolean;
  setOpenSidebarBackdrop?: (value: boolean) => void;
  count: number;
  setCount: (value: number) => void;
}) => {
  const [lesson, setLesson] = useState<Lesson>();
  const [openBackdrop, setOpenBackdrop] = useState<boolean>();

  useEffect(() => {
    setLesson(lessons.find((lesson: Lesson) => lesson.id === count));
    if (openHeaderBackdrop || openSidebarBackdrop) {
      setOpenBackdrop(true);
    }
  }, [count, openHeaderBackdrop, openSidebarBackdrop]);

  const handleSkip = () => {
    localStorage.removeItem('study');
    setOpenBackdrop(false);
    setOpenHeaderBackdrop?.(false);
    setOpenSidebarBackdrop?.(false);
  };

  const handleNext = () => {
    const newCount = count + 1;
    setCount(newCount);
    localStorage.setItem('study', newCount.toString());
  };

  if (!openBackdrop) return null;

  return (
    <Backdrop
      open={openBackdrop}
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Stack
        sx={{
          width: '700px',
          minHeight: '285px',
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Stack sx={{ width: '700px', borderBottom: '2px solid #13151940', padding: '10px 0' }}>
          <Typography sx={{ fontSize: '20px', fontWeight: 700, textAlign: 'center' }}>
            Learn & Start
          </Typography>
        </Stack>

        <Stack
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 20px',
          }}
        >
          <Typography sx={{ fontSize: '18px', textAlign: 'center' }}>{lesson?.title}</Typography>
          <Typography sx={{ fontSize: '16px', textAlign: 'center' }}>
            {lesson?.description}
          </Typography>
        </Stack>

        <Stack
          direction='row'
          spacing={3}
          sx={{
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '2px solid #13151940',
            padding: '20px',
          }}
        >
          <StyledButton
            style={{ width: '100px' }}
            type='submit'
            variant='contained'
            color='primary'
            onClick={handleNext}
          >
            Next
          </StyledButton>
          <StyledButtonOutlined
            style={{ width: '100px' }}
            type='submit'
            variant='contained'
            color='primary'
            onClick={handleSkip}
          >
            Skip
          </StyledButtonOutlined>
        </Stack>
      </Stack>
    </Backdrop>
  );
};
